import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { z } from 'zod';
import {
  CLIENT_STATUS_LABELS,
  CLIENT_STATUSES,
  clientInput,
  LABEL_COLORS,
  type ClientDto,
  type LabelColorKey,
} from '@cw/shared';
import { Button } from '@/design-system/components/button';
import { Dialog, DialogClose, DialogContent, DialogFooter } from '@/design-system/components/dialog';
import { Field } from '@/design-system/components/field';
import { Input, Textarea } from '@/design-system/components/input';
import { Select } from '@/design-system/components/select';
import { api } from '@/lib/api';
import { clientKeys } from '@/lib/clients';
import { handleFormError } from '@/lib/forms';
import { useMembers } from '@/lib/members';
import { cn } from '@/lib/utils';
import { formatPhone } from './whatsapp';

type FormIn = z.input<typeof clientInput>;
type FormOut = z.output<typeof clientInput>;

const COLOR_NAMES: Record<LabelColorKey, string> = {
  cyan: 'Ciano',
  amber: 'Âmbar',
  lavender: 'Lavanda',
  mint: 'Menta',
  coral: 'Coral',
  sky: 'Céu',
  rose: 'Rosa',
  sand: 'Areia',
};

export function ColorPicker({
  value,
  onChange,
}: {
  value: LabelColorKey;
  onChange: (c: LabelColorKey) => void;
}) {
  return (
    <div role="radiogroup" aria-label="Cor" className="flex flex-wrap gap-2">
      {LABEL_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={value === c}
          aria-label={COLOR_NAMES[c]}
          onClick={() => onChange(c)}
          className={cn(
            'size-7 rounded-full transition-transform hover:scale-110',
            value === c && 'ring-2 ring-fg ring-offset-2 ring-offset-raised',
          )}
          style={{ background: `var(--color-label-${c})` }}
        />
      ))}
    </div>
  );
}

function defaults(c?: ClientDto): FormIn {
  return {
    name: c?.name ?? '',
    brandName: c?.brandName ?? '',
    legalName: c?.legalName ?? '',
    taxId: c?.taxId ?? '',
    color: c?.color ?? LABEL_COLORS[Math.floor(Math.random() * LABEL_COLORS.length)]!,
    email: c?.email ?? '',
    whatsapp: c?.whatsapp ? formatPhone(c.whatsapp) : '',
    phone: c?.phone ? formatPhone(c.phone) : '',
    website: c?.website ?? '',
    status: c?.status ?? 'active',
    notes: c?.notes ?? '',
    ownerUserId: c?.owner?.id ?? null,
    address: c?.address ?? null,
  };
}

/** Cadastro e edição de cliente. Dados essenciais à vista; o resto em "Mais dados". */
export function ClientFormDialog({
  open,
  onOpenChange,
  client,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  client?: ClientDto;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const members = useMembers();
  const [more, setMore] = useState(
    Boolean(client?.legalName || client?.taxId || client?.address || client?.website),
  );
  const form = useForm<FormIn, unknown, FormOut>({
    resolver: zodResolver(clientInput),
    defaultValues: defaults(client),
  });
  const e = form.formState.errors;

  const save = useMutation({
    mutationFn: (v: FormOut) =>
      client ? api.patch<ClientDto>(`/clients/${client.id}`, v) : api.post<ClientDto>('/clients', v),
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: clientKeys.all });
      onOpenChange(false);
      toast.success(client ? 'Cliente salvo' : 'Cliente cadastrado');
      if (!client) navigate(`/clients/${saved.id}`);
    },
    onError: (err) => handleFormError(err, form.setError),
  });

  const owners = (members.data ?? [])
    .filter((m) => m.status === 'active' && m.role !== 'freelancer')
    .map((m) => ({ value: m.userId, label: m.name }));

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (o) form.reset(defaults(client));
      }}
    >
      <DialogContent title={client ? 'Editar cliente' : 'Novo cliente'} className="max-w-xl">
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          className="flex max-h-[70dvh] flex-col gap-4 overflow-y-auto pr-1 -mr-1"
          noValidate
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome" error={e.name?.message}>
              <Input autoFocus placeholder="Como a equipe chama o cliente" {...form.register('name')} />
            </Field>
            <Field label="Marca" hint="Se for diferente do nome.">
              <Input {...form.register('brandName')} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Status">
              <Controller
                control={form.control}
                name="status"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    options={CLIENT_STATUSES.map((s) => ({ value: s, label: CLIENT_STATUS_LABELS[s] }))}
                  />
                )}
              />
            </Field>
            <Field label="Responsável na equipe">
              <Controller
                control={form.control}
                name="ownerUserId"
                render={({ field }) => (
                  <Select
                    value={field.value ?? 'none'}
                    onValueChange={(v) => field.onChange(v === 'none' ? null : v)}
                    options={[{ value: 'none', label: 'Ninguém' }, ...owners]}
                  />
                )}
              />
            </Field>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-[13px] font-medium text-fg-muted">Cor de identificação</span>
            <Controller
              control={form.control}
              name="color"
              render={({ field }) => <ColorPicker value={field.value} onChange={field.onChange} />}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="E-mail" error={e.email?.message}>
              <Input type="email" {...form.register('email')} />
            </Field>
            <Field label="WhatsApp" error={e.whatsapp?.message}>
              <Input inputMode="tel" placeholder="(11) 98888-7777" {...form.register('whatsapp')} />
            </Field>
          </div>
          <Field label="Observações" hint="Só a equipe vê.">
            <Textarea rows={3} {...form.register('notes')} />
          </Field>

          <button
            type="button"
            onClick={() => setMore(!more)}
            className="flex items-center gap-1.5 self-start text-[13px] text-fg-muted hover:text-fg"
            aria-expanded={more}
          >
            <ChevronDown className={cn('size-4 transition-transform', !more && '-rotate-90')} /> Mais dados
          </button>
          {more && (
            <div className="grid gap-4 animate-in sm:grid-cols-2">
              <Field label="Razão social">
                <Input {...form.register('legalName')} />
              </Field>
              <Field label="CNPJ / CPF">
                <Input {...form.register('taxId')} />
              </Field>
              <Field label="Telefone">
                <Input inputMode="tel" {...form.register('phone')} />
              </Field>
              <Field label="Site">
                <Input placeholder="https://" {...form.register('website')} />
              </Field>
              <Field label="Endereço" className="sm:col-span-2">
                <Input {...form.register('address.street')} />
              </Field>
              <Field label="Cidade">
                <Input {...form.register('address.city')} />
              </Field>
              <Field label="Estado">
                <Input {...form.register('address.state')} />
              </Field>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button">Cancelar</Button>
            </DialogClose>
            <Button type="submit" variant="primary" loading={save.isPending}>
              {client ? 'Salvar cliente' : 'Cadastrar cliente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
